import { Button } from '../../components/ui';
import { ProofImage } from './ProofImage';

/**
 * The client's certificate of approval, printable.
 *
 * Two audiences, two payloads — see 20261102000100. TAP's carries the
 * signatory; the factory's carries the number, the date and the certified
 * artwork with its content hashes, and nothing about who bought it.
 *
 * This component renders whichever it is handed and never asks. It cannot leak
 * a field it was not given, which is the point of splitting at the database
 * rather than hiding in the markup: a `{isSupplier ? null : name}` here would
 * still have fetched the name into the browser.
 */

export interface CertificateAsset {
  component_type: string;
  version: number;
  url: string;
  content_hash?: string | null;
}

export interface Certificate {
  certificate_no: string;
  signed_at: string;
  order_no?: string;
  /** Present only on TAP's payload. */
  client_name?: string;
  client_title?: string;
  client_email?: string;
  client?: string;
  assets: CertificateAsset[];
}

export function CertificateView({
  certificate,
  onClose,
}: {
  certificate: Certificate;
  onClose: () => void;
}) {
  function print() {
    document.body.classList.add('po-printing');
    window.addEventListener('afterprint', () => document.body.classList.remove('po-printing'), {
      once: true,
    });
    window.print();
  }

  const signatory = certificate.client_name ?? '';

  return (
    // Same print isolation as the PO. Without this class the print stylesheet
    // hides the whole app tree and produces a blank page.
    <div className="po-doc-wrap po-print-root">
      {/* po-no-print, or Close and Download/Print appear on the saved PDF —
          which they did. A certificate is evidence; buttons on it read as a
          screenshot of an app rather than a document. */}
      <div className="po-doc-actions po-no-print">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
        <Button size="sm" onClick={print}>
          Download / Print
        </Button>
      </div>

      <article className="po-doc">
        <header className="po-doc-head">
          <h2>Certificate of Client Approval</h2>
          <dl>
            <dt>Certificate</dt>
            <dd>{certificate.certificate_no}</dd>
            <dt>Signed</dt>
            <dd>{certificate.signed_at.slice(0, 10)}</dd>
            {certificate.order_no == null ? null : (
              <>
                <dt>Job order</dt>
                <dd>{certificate.order_no}</dd>
              </>
            )}
            {/* Only ever present on TAP's payload. The factory's function does
                not select these columns at all. */}
            {signatory === '' ? null : (
              <>
                <dt>Signed by</dt>
                <dd>
                  {signatory}
                  {certificate.client_title == null || certificate.client_title === ''
                    ? ''
                    : `, ${certificate.client_title}`}
                </dd>
              </>
            )}
            {certificate.client_email == null || certificate.client_email === '' ? null : (
              <>
                <dt>Email</dt>
                <dd>{certificate.client_email}</dd>
              </>
            )}
          </dl>
        </header>

        <p>
          The artwork below was approved by the client and locked on{' '}
          {certificate.signed_at.slice(0, 10)}. These are the exact versions signed off.
        </p>

        <section>
          <h3>Certified artwork</h3>
          <ul className="po-doc-proofs">
            {certificate.assets.map((asset) => (
              <li key={`${asset.component_type}-${String(asset.version)}`}>
                <ProofImage url={asset.url} alt={asset.component_type} />
                <span>
                  {asset.component_type} v{asset.version}
                </span>
                {/* The hash is what turns a picture into evidence: a factory
                    holding it can show the file they built from is the file
                    that was approved. */}
                {asset.content_hash == null || asset.content_hash === '' ? null : (
                  <span className="po-doc-hash">{asset.content_hash.slice(0, 12)}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      </article>
    </div>
  );
}
